import { TagColor } from "@/features/tags/types";
import { UserSettings } from "@/services/api/fetchers/user-settings";

const USER_SETTINGS = "user-settings";

export const initialUserSettings: UserSettings = {
  app: {
    theme: "system",
    encryption: false,
    onboardingDone: false,
  },
  notes: {
    viewMode: "grid",
    ttsLanguage: "en-US",
    editorToolbarItems: [
      { id: "undo", enabled: true },
      { id: "redo", enabled: true },
      { id: "bold", enabled: true },
      { id: "italic", enabled: true },
      { id: "underline", enabled: true },
      { id: "strikethrough", enabled: true },
      { id: "textColor", enabled: true },
      { id: "backgroundColor", enabled: true },
      { id: "paragraph", enabled: true },
      { id: "heading1", enabled: true },
      { id: "heading2", enabled: true },
      { id: "heading3", enabled: true },
      { id: "bulletList", enabled: true },
      { id: "numberedList", enabled: true },
      { id: "checkList", enabled: true },
      { id: "quote", enabled: false },
      { id: "link", enabled: false },
      { id: "code", enabled: false },
      { id: "table", enabled: false },
      { id: "horizontalRule", enabled: false },
      { id: "indent", enabled: false },
      { id: "outdent", enabled: false },
    ],
  },
  todos: {
    filters: {
      period: "upcoming",
    },
  },
};

function getUserSettings(): UserSettings {
  const appSettings = localStorage.getItem(USER_SETTINGS);
  if (appSettings) {
    const parsedSettings = JSON.parse(appSettings) as UserSettings;
    return {
      ...initialUserSettings,
      ...parsedSettings,
      app: { ...initialUserSettings.app, ...parsedSettings.app },
      notes: { ...initialUserSettings.notes, ...parsedSettings.notes },
      todos: { ...initialUserSettings.todos, ...parsedSettings.todos },
    };
  }
  return initialUserSettings;
}

function setUserSettings(settings: Partial<UserSettings>) {
  const appSettings = getUserSettings();
  const newSettings = { ...appSettings, ...settings };
  localStorage.setItem(USER_SETTINGS, JSON.stringify(newSettings));
}

function getPwaAlertDismissed() {
  return localStorage.getItem("pwaAlertDismissed") === "true";
}

function setPwaAlertDismissed() {
  localStorage.setItem("pwaAlertDismissed", "true");
}

interface NotesNavigationState {
  scrollPosition: number;
  focusedIndex: number;
  wasKeyboard: boolean;
}

function setNotesNavigationState(newState: Partial<NotesNavigationState>) {
  const state = getNotesNavigationState();
  const updatedState = { ...state, ...newState };
  localStorage.setItem("notes-navigation-state", JSON.stringify(updatedState));
}

function getNotesNavigationState(): NotesNavigationState {
  const state = localStorage.getItem("notes-navigation-state");

  if (state) {
    const parsedState = JSON.parse(state) as NotesNavigationState;
    return { ...parsedState, scrollPosition: Number(parsedState.scrollPosition) };
  }

  return { scrollPosition: 0, focusedIndex: -1, wasKeyboard: false };
}

function removeNotesScrollPosition() {
  localStorage.removeItem("notes-navigation-state");
}

interface NotesSearchState {
  text: string;
  tags: Array<{ id: number; color: TagColor | null; name: string; encrypted_data: string | null; iv: string | null }>;
}

function setNotesSearch(state: NotesSearchState) {
  localStorage.setItem(
    "notes-search",
    JSON.stringify({ ...state, text: state.text.startsWith("#") ? "" : state.text })
  );
}

function getNotesSearch(): NotesSearchState {
  const state = localStorage.getItem("notes-search");
  return state ? JSON.parse(state) : { text: "", tags: [] };
}

function setLastSync(timestamp: string) {
  localStorage.setItem("last-sync-notes", timestamp);
}

function getLastSync(): string | null {
  return localStorage.getItem("last-sync-notes");
}

function removeLastSync() {
  localStorage.removeItem("last-sync-notes");
}

function setLastForceSync(timestamp: string) {
  localStorage.setItem("last-force-sync", timestamp);
}

function getLastForceSync(): string | null {
  return localStorage.getItem("last-force-sync");
}

function removeLastForceSync() {
  localStorage.removeItem("last-force-sync");
}

function getPersistentStoragePermissionAsked() {
  return localStorage.getItem("persistentStoragePermissionAsked") === "true";
}

function setPersistentStoragePermissionAsked() {
  localStorage.setItem("persistentStoragePermissionAsked", "true");
}

export const storage = {
  getUserSettings,
  setUserSettings,
  getPwaAlertDismissed,
  setPwaAlertDismissed,
  setNotesNavigationState,
  getNotesNavigationState,
  removeNotesScrollPosition,
  setNotesSearch,
  getNotesSearch,
  setLastSync,
  getLastSync,
  removeLastSync,
  setLastForceSync,
  getLastForceSync,
  removeLastForceSync,
  getPersistentStoragePermissionAsked,
  setPersistentStoragePermissionAsked,
};
